import React from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { PoweredByInvictaX } from "@/components/PoweredByInvictaX";
import { useTranslation } from "react-i18next";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { SimpleSelect } from "@/components/ui/simple-select";
import CompanySwitcher, { rememberLastPage } from "@/components/CompanySwitcher";
import AssistantWidget from "@/components/AssistantWidget";
import NotificationBell from "@/components/NotificationBell";
import { UploadProgressBar } from "@/components/UploadProgressBar";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { apiClient } from "@/lib/apiClient";
import CommandPalette from "@/components/CommandPalette";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { ResetAccountingDialog } from "@/components/ResetAccountingDialog";
import { Eraser, ShieldOff, FolderOpen, Percent , ClipboardCheck } from "lucide-react";
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts";
import { ShortcutsCheatsheet } from "@/components/ShortcutsCheatsheet";
import {
  LayoutDashboard,
  Receipt,
  FileText,
  BookOpen,
  CalendarCheck,
  CreditCard,
  BarChart3,
  FileInput,
  Landmark,
  Wallet,
  Coins,
  Package,
  Settings as SettingsIcon,
  LogOut,
  HelpCircle,
  ShieldCheck,
  Building2,
  Briefcase,
  UserCog,
  Timer,
  FileSpreadsheet,
  Truck,
  UserSquare2,
  FileSignature,
  Bell,
  ChevronDown,
  ChevronRight,
  Search,
  Plus,
  Clock,
  Code2,
  Inbox as InboxIcon,
  Banknote,
  Upload,
  Repeat,
  Shield,
  Boxes,
} from "lucide-react";

const NAV_GROUPS = [
  {
    id: "overview",
    labelKey: "nav.groups.overview",
    items: [
      { to: "/", labelKey: "nav.items.dashboard", icon: LayoutDashboard, testid: "nav-dashboard-link", end: true },
      { to: "/treuhand/cockpit", label: "Treuhänder-Portal", icon: Briefcase, testid: "nav-trustee-portal-link" },
      { to: "/cockpit", labelKey: "nav.items.cockpit", icon: Briefcase, testid: "nav-cockpit-link" },
      { to: "/portfolio", labelKey: "nav.items.portfolio", icon: Building2, testid: "nav-portfolio-link" },
      { to: "/holding", label: "Holding & Konzern", icon: Building2, testid: "nav-holding-link" },
    ],
  },
  {
    id: "daily",
    labelKey: "nav.groups.daily",
    items: [
      { to: "/inbox", labelKey: "nav.items.inbox", label: "Posteingang", icon: InboxIcon, testid: "nav-inbox-link", badge: "neu" },
      { to: "/receipts", labelKey: "nav.items.receipts", icon: Receipt, testid: "nav-receipts-link" },
      { to: "/quotes", label: "Offerten", icon: FileSignature, testid: "nav-quotes-link" },
      { to: "/orders", label: "Aufträge & Lieferscheine", icon: ClipboardCheck, testid: "nav-orders-link" },
      { to: "/invoices", labelKey: "nav.items.invoices", icon: FileText, testid: "nav-invoices-link" },
      { to: "/recurring-invoices", label: "Wiederkehrend", icon: Repeat, testid: "nav-recurring-invoices-link" },
      { to: "/dunning", labelKey: "nav.items.dunning", icon: Bell, testid: "nav-dunning-link" },
      { to: "/bills", labelKey: "nav.items.bills", icon: FileInput, testid: "nav-bills-link" },
      { to: "/loans", label: "Darlehen", icon: Banknote, testid: "nav-loans-link" },
      { to: "/bank-reconciliation", labelKey: "nav.items.bank_reconciliation", icon: Landmark, testid: "nav-bank-reconciliation-link" },
      { to: "/cashbook", labelKey: "nav.items.cashbook", icon: Wallet, testid: "nav-cashbook-link" },
      { to: "/journal", labelKey: "nav.items.journal", icon: BookOpen, testid: "nav-journal-link" },
      { to: "/auto-bookings", label: "Auto-Buchungen", icon: Repeat, testid: "nav-auto-bookings-link" },
      { to: "/warranties", label: "Garantien", icon: Shield, testid: "nav-warranties-link" },
      { to: "/contracts", label: "Verträge", icon: FileSignature, testid: "nav-contracts-link" },
      { to: "/documents", label: "Dokumente", icon: FolderOpen, testid: "nav-documents-link" },
      { to: "/inventory", label: "Inventar", icon: Boxes, testid: "nav-inventory-link" },
    ],
  },
  {
    id: "projects",
    labelKey: "nav.groups.projects",
    items: [
      { to: "/projects", labelKey: "nav.items.projects", icon: Briefcase, testid: "nav-projects-link" },
      { to: "/time-entries", labelKey: "nav.items.time_entries", icon: Clock, testid: "nav-time-entries-link" },
    ],
  },
  {
    id: "payroll",
    labelKey: "nav.groups.payroll",
    items: [
      { to: "/payroll", labelKey: "nav.items.payroll", icon: Coins, testid: "nav-payroll-link" },
      { to: "/employees", labelKey: "nav.items.employees", icon: UserCog, testid: "nav-employees-link" },
      { to: "/work-time", label: "Zeiterfassung MA", icon: Timer, testid: "nav-worktime-link" },
      { to: "/payroll-config", labelKey: "nav.items.payroll_config", icon: SettingsIcon, testid: "nav-payroll-config-link" },
      { to: "/payroll-report", labelKey: "nav.items.payroll_report", icon: FileSpreadsheet, testid: "nav-payroll-report-link" },
      { to: "/assets", labelKey: "nav.items.assets", icon: Package, testid: "nav-assets-link" },
    ],
  },
  {
    id: "master",
    labelKey: "nav.groups.master",
    items: [
      { to: "/customers", labelKey: "nav.items.customers", icon: UserSquare2, testid: "nav-customers-link" },
      { to: "/products", label: "Produkte & Lager", icon: Boxes, testid: "nav-products-link" },
      { to: "/lsv-mandates", labelKey: "nav.items.lsv_mandates", icon: FileSignature, testid: "nav-lsv-mandates-link" },
      { to: "/suppliers", labelKey: "nav.items.suppliers", icon: Truck, testid: "nav-suppliers-link" },
    ],
  },
  {
    id: "closing",
    labelKey: "nav.groups.closing",
    items: [
      { to: "/reports", labelKey: "nav.items.reports", icon: BarChart3, testid: "nav-reports-link" },
      { to: "/payment-fees", label: "Zahlungsgebühren", icon: Percent, testid: "nav-payment-fees-link" },
      { to: "/subscriptions", label: "Abo-Übersicht", icon: Repeat, testid: "nav-subscriptions-link" },
      { to: "/year-end", labelKey: "nav.items.year_end", icon: CalendarCheck, testid: "nav-year-end-link" },
      { to: "/opening-balance", label: "Eröffnungsbilanz", icon: Upload, testid: "nav-opening-balance-link" },
      { to: "/compliance", label: "Compliance & GeBüV", icon: ShieldCheck, testid: "nav-compliance-link" },
    ],
  },
  {
    id: "system",
    labelKey: "nav.groups.system",
    items: [
      { to: "/settings", labelKey: "nav.items.settings", icon: SettingsIcon, testid: "nav-settings-link" },
      { to: "/billing", label: "Abo & Tarife", icon: CreditCard, testid: "nav-billing-link" },
      { to: "/developers", labelKey: "nav.items.developers", icon: Code2, testid: "nav-developers-link" },
    ],
  },
];

// Iter123: Super-Admin sieht nur System-Verwaltung — keine Buchhaltungsdaten der Kunden.
// Iter130: /settings entfernt (ist Firmen-spezifisch und beim SA sinnlos)
// Iter132: /portfolio entfernt (SA hat keine eigenen Firmen; die Firmen-Übersicht
//          ist bereits als Kachel im Super-Admin-Cockpit erreichbar).
const SUPER_ADMIN_NAV_GROUPS = [
  {
    id: "platform",
    labelKey: "nav.groups.system",
    items: [
      { to: "/super-admin", label: "Super-Admin", icon: ShieldCheck, testid: "nav-super-admin-link", end: true },
      { to: "/account", label: "Mein Account", icon: UserCog, testid: "nav-account-link" },
    ],
  },
];

// Gruppen, die standardmässig geschlossen starten (Benutzer kann sie jederzeit aufklappen).
const DEFAULT_COLLAPSED_GROUPS = new Set([
  "projects",
  "payroll",
  "master",
  "closing",
  "system",
]);

const PRIVAT_HIDDEN_PATHS = new Set(["/quotes", "/orders", "/invoices", "/recurring-invoices", "/dunning", "/lsv-mandates", "/customers", "/products", "/suppliers", "/holding", "/portfolio", "/payroll", "/employees", "/work-time", "/payroll-config", "/payroll-report", "/projects", "/time-entries", "/opening-balance"]);
const PRIVAT_GROUP_LABELS = { payroll: "Vermögen", daily: "Alltag" };
// Milchbüchlein-Modus: Bilanz-/Konzern-/Abschluss-Fachfunktionen ausblenden – Daten bleiben vollständig vorhanden.
const SIMPLE_HIDDEN_PATHS = new Set(["/cockpit", "/portfolio", "/holding", "/quotes", "/orders", "/recurring-invoices", "/dunning", "/loans", "/journal", "/auto-bookings", "/warranties", "/contracts", "/inventory",
  "/projects", "/time-entries", "/payroll", "/employees", "/work-time", "/payroll-config", "/payroll-report", "/assets", "/products", "/lsv-mandates", "/reports", "/payment-fees", "/year-end", "/opening-balance", "/compliance", "/developers"]);
const SIMPLE_GROUP_LABELS = { daily: "Alltag", master: "Kontakte", closing: "Übersicht" };

function navGroupsFor(company) {
  if (company?.accounting_mode === "simple" && company?.company_type !== "privat") {
    return NAV_GROUPS.map((g) => ({
      ...g,
      label: SIMPLE_GROUP_LABELS[g.id],
      items: g.id === "daily"
        ? [{ to: "/simple-ledger", label: "Einnahmen & Ausgaben", icon: Wallet, testid: "nav-simple-ledger-link" }, ...g.items.filter((i) => !SIMPLE_HIDDEN_PATHS.has(i.to))]
        : g.items.filter((i) => !SIMPLE_HIDDEN_PATHS.has(i.to)),
    })).filter((g) => g.items.length > 0);
  }
  if (company?.company_type !== "privat") return NAV_GROUPS;
  return NAV_GROUPS.map((g) => ({ ...g, label: PRIVAT_GROUP_LABELS[g.id], items: g.items.filter((i) => !PRIVAT_HIDDEN_PATHS.has(i.to)) })).filter((g) => g.items.length > 0);
}

function NavSection({ group }) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(!DEFAULT_COLLAPSED_GROUPS.has(group.id));
  return (
    <div className="mb-2" data-testid={`nav-group-${group.id}`}>
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold tracking-wider uppercase text-slate-400 hover:text-slate-600 transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-group-toggle-${group.id}`}
      >
        <span>{group.label || t(group.labelKey)}</span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
      </button>
      {open && (
        <div className="space-y-0.5">
          {group.items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2 text-base rounded-lg transition-colors ${
                    isActive
                      ? "bg-slate-900 text-slate-50 shadow-sm"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
                data-testid={item.testid}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span className="truncate">{item.label || t(item.labelKey)}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MainLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setEmail, email, logout } = useAuth();
  const { t } = useTranslation();
  const [isSuperAdmin, setIsSuperAdmin] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    import("@/lib/apiClient").then(({ apiClient }) => {
      apiClient.get("/auth/me").then((r) => {
        if (active) setIsSuperAdmin(!!r.data?.is_super_admin);
      }).catch(() => { /* nicht eingeloggt — ignore */ });
    });
    return () => { active = false; };
  }, []);

  // Iter123: Super-Admin landet immer im Super-Admin Cockpit – keine Mandanten-Daten.
  React.useEffect(() => {
    if (!isSuperAdmin) return;
    const path = location.pathname;
    const allowed = path.startsWith("/super-admin")
      || path.startsWith("/portfolio")
      || path.startsWith("/settings")
      || path.startsWith("/account")
      || path.startsWith("/login");
    if (!allowed) {
      navigate("/super-admin", { replace: true });
    }
  }, [isSuperAdmin, location.pathname, navigate]);

  const handleLogoutEverywhere = async () => {
    if (!window.confirm("Alle aktiven Sitzungen auf allen Geräten sofort beenden? Du musst dich danach neu anmelden.")) return;
    try {
      await apiClient.post("/auth/logout-everywhere");
      toast.success("Alle Sitzungen beendet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Abmelden überall fehlgeschlagen");
      return;
    }
    handleLogout();
  };

  const handleLogout = async () => {
    localStorage.removeItem("demoCompanyId");
    await logout();
    navigate("/login");
  };

  const { company, setCompany } = useCompany();
  const [companies, setCompanies] = React.useState([]);

  // Iter108: Last-Page-Restore — merke die aktuelle Route pro Mandant
  React.useEffect(() => {
    if (company?._id && location.pathname && !location.pathname.startsWith("/login")) {
      rememberLastPage(company._id, location.pathname + (location.search || ""));
    }
  }, [company?._id, location.pathname, location.search]);

  React.useEffect(() => {
    let isMounted = true;
    const loadCompanies = async () => {
      try {
        const res = await apiClient.get("/my/companies");
        const data = res.data;
        if (isMounted) {
          setCompanies(data || []);
          // Iter259: Portal-Mitarbeiter (keine Firmen) → nur eigene Zeiterfassung
          if ((data || []).length === 0) {
            try {
              const p = await apiClient.get("/my/portal/profile");
              if (p.data?.is_employee) {
                window.location.replace("/my-time");
                return;
              }
            } catch { /* ignore */ }
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    loadCompanies();
    return () => {
      isMounted = false;
    };
  }, []);

  const companyName = company?.name || t("sidebar.brand_default_company");
  const currency = company?.currency || "CHF";

  // ⌘K Global Search + Iter105: Sequence-Shortcuts (G+R, G+I, ...) + Shift+? Cheatsheet
  const [cmdOpen, setCmdOpen] = React.useState(false);
  const [cheatsheetOpen, setCheatsheetOpen] = React.useState(false);
  const [resetOpen, setResetOpen] = React.useState(false);
  useKeyboardShortcuts({
    onOpenSearch: () => setCmdOpen((o) => !o),
    onOpenCheatsheet: () => setCheatsheetOpen(true),
  });

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      <aside
        className="w-64 border-r border-slate-200 bg-white flex flex-col"
        data-testid="sidebar-nav"
      >
        <div className="px-3 py-3 border-b border-slate-200">
          {!isSuperAdmin && companies.length > 0 ? (
            <CompanySwitcher
              companies={companies}
              activeCompany={company}
              onSelect={(selected) => {
                if (selected._id === company?._id) return;
                setCompany(selected);
                localStorage.setItem("demoCompanyId", selected._id);
                toast.success(`Gewechselt zu ${selected.name}`);
              }}
              variant="sidebar"
            />
          ) : (
            <div className="flex items-center gap-2.5 px-2 py-1.5" data-testid="sidebar-brand">
              <div className={`h-10 w-10 rounded-lg ${isSuperAdmin ? "bg-amber-600" : "bg-slate-900"} text-white flex items-center justify-center shadow-sm`}>
                {isSuperAdmin ? <ShieldCheck className="h-6 w-6" /> : <Building2 className="h-6 w-6" />}
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold tracking-wider text-slate-400 uppercase">
                  {isSuperAdmin ? "Plattform" : t("sidebar.brand_label")}
                </div>
                <div
                  className="text-base font-semibold text-slate-900 truncate"
                  data-testid="sidebar-company-name"
                  title={isSuperAdmin ? "Super-Admin" : companyName}
                >
                  {isSuperAdmin ? "Super-Admin Cockpit" : companyName}
                </div>
              </div>
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-3 overflow-y-auto">
          {(isSuperAdmin ? SUPER_ADMIN_NAV_GROUPS : navGroupsFor(company)).map((group) => (
            <NavSection key={group.id} group={group} />
          ))}
        </nav>
        <div className="px-4 py-3 border-t border-slate-200 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-full bg-emerald-500" />
            <span>{t("sidebar.footer")}</span>
          </div>
          <PoweredByInvictaX className="mt-1.5" />
        </div>
      </aside>
      <main className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-slate-200 bg-white/70 backdrop-blur flex items-center justify-between px-6 sticky top-0 z-10">
          <div className="flex items-center gap-3 text-sm text-slate-500" data-testid="topbar-context">
            {!isSuperAdmin && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                {currency}
              </span>
            )}
            {!isSuperAdmin && companies.length > 0 && (
              <CompanySwitcher
                companies={companies}
                activeCompany={company}
                onSelect={(selected) => {
                  if (selected._id === company?._id) return;
                  setCompany(selected);
                  localStorage.setItem("demoCompanyId", selected._id);
                  toast.success(`Gewechselt zu ${selected.name}`);
                }}
              />
            )}
            {!isSuperAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/new-company")}
              className="h-8 px-2.5 text-xs"
              data-testid="topbar-new-company-button"
              title={t("topbar.new_company_title")}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              {t("topbar.new_company")}
            </Button>
            )}
            {!isSuperAdmin && company && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setResetOpen(true)}
                className="h-8 px-2.5 text-xs text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                data-testid="topbar-reset-accounting-button"
                title="Alle Belege, Rechnungen, Bank- & Journal-Buchungen dieser Firma endgültig löschen"
              >
                <Eraser className="h-3.5 w-3.5 mr-1" />
                Daten-Reset
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCmdOpen(true)}
              data-testid="topbar-search-button"
              className="text-slate-500 pr-3"
            >
              <Search className="h-4 w-4 mr-1.5" />
              <span className="hidden md:inline">{t("topbar.search")}</span>
              <kbd className="ml-2 hidden md:inline-flex items-center gap-0.5 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-mono text-slate-500">
                ⌘K
              </kbd>
            </Button>
            <UploadProgressBar />
            <NotificationBell />
            {email && (
              <button
                type="button"
                onClick={() => navigate("/account")}
                className="text-sm text-slate-600 hover:text-slate-900 hover:underline mr-2 truncate max-w-[180px]"
                data-testid="topbar-user-email"
                title={`${email} — Mein Account`}
              >
                {email}
              </button>
            )}
            <LanguageSwitcher />
            {isSuperAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/super-admin")}
                data-testid="topbar-super-admin-link"
                className="text-amber-700 border-amber-200 hover:bg-amber-50"
                title="Super-Admin Dashboard"
              >
                <ShieldCheck className="h-4 w-4 mr-1.5" />
                Super-Admin
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              data-testid="topbar-partner-link"
              onClick={() => window.open("/partner", "_blank", "noopener,noreferrer")}
              className={`text-slate-600 hover:text-indigo-700 ${isSuperAdmin ? "hidden" : ""}`}
            >
              {t("topbar.partner")}
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="topbar-help-button"
              onClick={() => window.open("/settings", "_self")}
            >
              <HelpCircle className="h-4 w-4 mr-1.5" />
              {t("topbar.help")}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogoutEverywhere}
              title="Alle Sitzungen auf allen Geräten beenden"
              data-testid="topbar-logout-everywhere-button"
            >
              <ShieldOff className="h-4 w-4 mr-1.5" />
              Überall abmelden
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="topbar-logout-button"
            >
              <LogOut className="h-4 w-4 mr-1.5" />
              {t("topbar.logout")}
            </Button>
          </div>
        </header>
        <section className="flex-1 p-6" data-testid="main-content">
          {/* Iter82 Bug-Fix: `key` zwingt React zu komplettem Remount jeder Route
              beim Firmen-Wechsel. Garantiert frisches Fetch mit korrekter
              company_id — 24 Seiten lesen localStorage.getItem direkt und sind
              ohne Remount nicht reaktiv auf Context-Änderungen. */}
          <Outlet key={company?._id || "no-company"} />
        </section>
      </main>
      <Toaster position="top-right" richColors />
      <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
      <ResetAccountingDialog open={resetOpen} onOpenChange={setResetOpen} company={company} />
      <ShortcutsCheatsheet open={cheatsheetOpen} onOpenChange={setCheatsheetOpen} />
      <AssistantWidget />
    </div>
  );
}
